import React, {useCallback, useContext, useEffect, useState} from "react"
import Row from "@amzn/meridian/row";
import Table, {TableCell, TableRow,} from "@amzn/meridian/table"
import {FormattedMessage} from "react-intl";

import {AR_V2, SORT_DIRECTION} from "../util/Constants";

import {CapabilityContext} from "../global/context/CapabilityContext";
import {WorkstationMetricsContext} from "./context/WorkstationMetricsContext"
import * as WorkstationMetricColumns from "./WorkstationMetricsTableColumns";
import {API_METRIC_NAME} from "./network/apiMetricNames";
import {
    emptyArrayIfNull,
    getRefreshInterval,
    openSCCPageInNewTab,
    sortListWithFixedTopRow
} from "../util/Util";
import Link from "@amzn/meridian/link";
import Text from "@amzn/meridian/text";
import {PickFilterContext} from "../pickStatus/context/PickFilterContext";
import * as FilterUtil from "../util/FilterUtil";


const WorkstationMetricsTable = (props) => {
    const {configViewModel} = useContext(CapabilityContext)
    const {metricsViewData, dispatchMetricsView} = useContext(WorkstationMetricsContext)
    const {viewModelF, dispatchF} = useContext(PickFilterContext)

    const [sortColumn, setSortColumn] = useState(API_METRIC_NAME.stations)
    const [sortDirection, setSortDirection] = useState(SORT_DIRECTION.DESC)
    const onSort = useCallback(({ sortColumn, sortDirection }) => {
        setSortDirection(sortDirection)
        setSortColumn(sortColumn)
    }, [])

    /** Create clone of global metrics list */
    let arMetricsRowList = Array.from(emptyArrayIfNull(metricsViewData.metricsTableData[metricsViewData.granularity]));
    arMetricsRowList = FilterUtil.applyARv2PickStationFilters(arMetricsRowList, viewModelF, configViewModel)

    /**
     * Sort metrics while keeping ALL row on top
     * ALL row provide metrics aggregated for all workstations in the selected time interval
     * **/
    const topRowInfo = {
        "column" : "stationID",
        "value" : "ALL"
    }
    sortListWithFixedTopRow(arMetricsRowList, sortColumn, sortDirection, topRowInfo);

    const getWorkstationMetricsData = () => {
        if (configViewModel.capability === AR_V2) {
            dispatchMetricsView({
                type: 'LOAD_VIEW',
                data: {
                    baseUrl: configViewModel.baseUrl,
                    dispatcher: dispatchMetricsView,
                    filterDispatcher: dispatchF,
                    stationConfig: configViewModel.stationConfig
                }
            });
        }
    }

    useEffect(() => {
        getWorkstationMetricsData();
        const interval = setInterval(() => {
            getWorkstationMetricsData()
        }, getRefreshInterval(configViewModel.stationConfig));
        return () => clearInterval(interval);
    }, []);

    const openAssociatePickTab = () => {
        openSCCPageInNewTab('pick', 'associatepick');
    }

    const getAssociateColForPickStation = (associateList) => {
        if (associateList.length) {
            const responseList = [];
            for (let i = 0; i < associateList.length && i < 2; i++) {
                responseList.push(<Row key={associateList[i]}>
                    <Link onClick={() => openAssociatePickTab()} type="secondary">{associateList[i]}</Link>
                    {i !== associateList.length - 1 ? "," : ""}&nbsp;
                </Row>)
            }
            if (associateList.length > 2) {
                responseList.push(<Row key={associateList[2]}><Text>...</Text></Row>);
            }
            return <Row overflowY={"hidden"} spacing={"none"}>{responseList}</Row>;
        } else {
            return "-";
        }
    }

    const renderHeaderRow = () => {
        return <TableRow>
            <TableCell sortColumn={API_METRIC_NAME.stations}>
                <FormattedMessage id="arWorkstationMetrics.stations" defaultMessage={WorkstationMetricColumns.STATIONS}/>
            </TableCell>
            <TableCell sortColumn={API_METRIC_NAME.associate}>
                <FormattedMessage id="arWorkstationMetrics.associate" defaultMessage={WorkstationMetricColumns.ASSOCIATE}/>
            </TableCell>
            <TableCell sortColumn={API_METRIC_NAME.stationStatus}>
                <FormattedMessage id="arWorkstationMetrics.stationStatus" defaultMessage={WorkstationMetricColumns.STATION_STATUS}/>
            </TableCell>
            <TableCell sortColumn={API_METRIC_NAME.pickedPackageCount}>
                <FormattedMessage id="arWorkstationMetrics.pickedPackageCount" defaultMessage={WorkstationMetricColumns.PICKED_PACKAGE_COUNT}/>
            </TableCell>
            <TableCell sortColumn={API_METRIC_NAME.podGap}>
                <FormattedMessage id="arWorkstationMetrics.podGap" defaultMessage={WorkstationMetricColumns.POD_GAP}/>
            </TableCell>
            <TableCell sortColumn={API_METRIC_NAME.podDwellTime}>
                <FormattedMessage id="arWorkstationMetrics.podDwellTime" defaultMessage={WorkstationMetricColumns.POD_DWELL_TIME}/>
            </TableCell>
            <TableCell sortColumn={API_METRIC_NAME.packagePileOnRate}>
                <FormattedMessage id="arWorkstationMetrics.packagePileOnRate" defaultMessage={WorkstationMetricColumns.PILE_ON}/>
            </TableCell>
            <TableCell sortColumn={API_METRIC_NAME.pickTaktTime}>
                <FormattedMessage id="arWorkstationMetrics.pickTaktTime" defaultMessage={WorkstationMetricColumns.PICK_TAKT_TIME}/>
            </TableCell>
            <TableCell sortColumn={API_METRIC_NAME.pickRate}>
                <FormattedMessage id="arWorkstationMetrics.pickRate" defaultMessage={WorkstationMetricColumns.PICK_RATE}/>
            </TableCell>
        </TableRow>
    }

    const renderMetricsRow = (rowData) => {
        return <TableRow key={`station_${rowData.stationID}`}>
            <TableCell>{rowData.stationID}</TableCell>
            <TableCell>{rowData[API_METRIC_NAME.associate] ? getAssociateColForPickStation(rowData[API_METRIC_NAME.associate]) : "-"}</TableCell>
            <TableCell>{rowData[API_METRIC_NAME.stationStatus] ? rowData[API_METRIC_NAME.stationStatus] : "-"}</TableCell>
            <TableCell>{rowData[API_METRIC_NAME.pickedPackageCount] ? rowData[API_METRIC_NAME.pickedPackageCount] : "-"}</TableCell>
            <TableCell
                alignmentHorizontal="left">{rowData[API_METRIC_NAME.podGap] ? rowData[API_METRIC_NAME.podGap] : "-"}</TableCell>
            <TableCell>{rowData[API_METRIC_NAME.podDwellTime] ? rowData[API_METRIC_NAME.podDwellTime] : "-"}</TableCell>
            <TableCell>{rowData[API_METRIC_NAME.packagePileOnRate] ? rowData[API_METRIC_NAME.packagePileOnRate] : "-"}</TableCell>
            <TableCell>{rowData[API_METRIC_NAME.pickTaktTime] ? rowData[API_METRIC_NAME.pickTaktTime] : "-"}</TableCell>
            <TableCell>{rowData[API_METRIC_NAME.pickRate] ? rowData[API_METRIC_NAME.pickRate] : "-"}</TableCell>
        </TableRow>
    }

    const renderMetricsTable = () => {
        return <Table
            showDividers={true}
            showStripes={false}
            headerRows={1}
            spacing={"small"}
            sortColumn={sortColumn}
            sortDirection={sortDirection}
            onSort={onSort}>
            {renderHeaderRow()}
            {arMetricsRowList.map((route, key) => renderMetricsRow(route))}
            {arMetricsRowList && arMetricsRowList.length !== 0 ? null : (
                <TableRow>
                    <TableCell columnSpan={9} alignmentHorizontal="center">No data</TableCell>
                </TableRow>
            ) }
        </Table>
    }

    return (
        <div>
            <Row width="100%" maxWidth="100%" spacingInset="medium none medium none">
                {renderMetricsTable()}
            </Row>
        </div>
    )
}

export default WorkstationMetricsTable


// WEBPACK FOOTER //
// ./src/components/workstationMetrics/MetricsTable.js